/**
 * Single source of truth for public, indexable routes.
 * Used by SEOHead (client), the build-time prerender, sitemap.xml and llms.txt.
 */
import { INDUSTRIAL_SECTOR_SLUGS, RETAIL_VENUE_SLUGS, SECTOR_LABELS_ES, VENUE_LABELS_ES } from "./slugs";

export const SITE_URL = "https://techtail.es";
export const LANGS = ["es", "en", "it", "de", "fr"] as const;
export type Lang = (typeof LANGS)[number];

export const LANG_META: Record<Lang, { hreflang: string; locale: string }> = {
  es: { hreflang: "es-ES", locale: "es_ES" },
  en: { hreflang: "en", locale: "en_GB" },
  it: { hreflang: "it-IT", locale: "it_IT" },
  de: { hreflang: "de-DE", locale: "de_DE" },
  fr: { hreflang: "fr-FR", locale: "fr_FR" },
};

export const DEFAULT_OG_IMAGE =
  "https://storage.googleapis.com/gpt-engineer-file-uploads/w27cBSf9NlT9WoogvrQcqxhbt5g2/social-images/social-1768906784335-Render-SmartStore-ampliado%20(1).jpg";

export type PageKind = "home" | "division" | "about" | "legal" | "venue" | "sector" | "service" | "tool" | "hub" | "article" | "case";

export interface SeoRoute {
  /** Path without language prefix, "" for home (the ES path when `paths` is set) */
  path: string;
  /** Per-language paths when slugs are translated (resources/cases). Omit = `path` in every language. */
  paths?: Partial<Record<Lang, string>>;
  /** Content with missing facts (marker listed in src/content/AGENTS.md): rendered with noindex,follow, no hreflang/JSON-LD, out of sitemap and llms.txt. */
  draft?: boolean;
  kind: PageKind;
  ogImage: string;
  /** Real date (YYYY-MM-DD) of the last significant content change. Omit if unknown. */
  updated?: string;
  priority: string;
  changefreq: "weekly" | "monthly" | "yearly";
  /** Languages with reviewed content. Others render (EN text) with noindex,follow, canonical to EN, and stay out of sitemap/hreflang. Omit = all. */
  langs?: Lang[];
  /** Parent route path for the BreadcrumbList (e.g. "retail"). */
  parent?: string;
  /** Short label for llms.txt */
  llms?: { title: string; description: string; optional?: boolean };
}

const og = (name: string) => `${SITE_URL}/og/${name}.jpg`;

/** Pages whose IT/DE/FR translation is not reviewed yet. */
const ES_EN: Lang[] = ["es", "en"];

export const SEO_ROUTES: SeoRoute[] = [
  { path: "", kind: "home", updated: "2026-11-01", ogImage: DEFAULT_OG_IMAGE, priority: "1.0", changefreq: "weekly",
    llms: { title: "Inicio", description: "Qué es Techtail y sus tres divisiones." } },
  { path: "retail", kind: "division", updated: "2026-11-01", ogImage: og("retail"), priority: "0.9", changefreq: "monthly",
    llms: { title: "Retail", description: "Tiendas autónomas 24/7 para espacios con tráfico." } },
  { path: "industrial", kind: "division", updated: "2026-11-01", ogImage: og("industrial"), priority: "0.9", changefreq: "monthly",
    llms: { title: "Industrial", description: "Almacenes y Smart Stock autónomo para industria." } },
  { path: "medical", kind: "division", ogImage: og("medical"), priority: "0.9", changefreq: "monthly",
    llms: { title: "Medical", description: "Soluciones autónomas para hospitales y laboratorios." } },
  { path: "compania", kind: "about", ogImage: og("compania"), priority: "0.7", changefreq: "monthly",
    llms: { title: "Compañía", description: "Equipo, fábrica en Barcelona y Grupo Moure." } },
  { path: "privacidad", kind: "legal", ogImage: DEFAULT_OG_IMAGE, priority: "0.3", changefreq: "yearly",
    llms: { title: "Privacidad", description: "Política de privacidad.", optional: true } },
  { path: "legal", kind: "legal", ogImage: DEFAULT_OG_IMAGE, priority: "0.3", changefreq: "yearly",
    llms: { title: "Aviso legal", description: "Aviso legal.", optional: true } },
  { path: "cookies", kind: "legal", ogImage: DEFAULT_OG_IMAGE, priority: "0.3", changefreq: "yearly",
    llms: { title: "Cookies", description: "Política de cookies.", optional: true } },
  { path: "canal-etico", kind: "legal", ogImage: DEFAULT_OG_IMAGE, priority: "0.3", changefreq: "yearly",
    llms: { title: "Canal ético", description: "Canal ético y de denuncias.", optional: true } },
  ...RETAIL_VENUE_SLUGS.map<SeoRoute>((slug) => ({
    path: `retail/${slug}`, kind: "venue", updated: "2026-11-01", parent: "retail", langs: ES_EN, ogImage: og("retail"), priority: "0.8", changefreq: "monthly",
    llms: { title: `Retail · ${VENUE_LABELS_ES[slug]}`, description: `Tienda autónoma 24/7 para ${VENUE_LABELS_ES[slug].toLowerCase()}.` },
  })),
  ...INDUSTRIAL_SECTOR_SLUGS.map<SeoRoute>((slug) => ({
    path: `industrial/${slug}`, kind: "sector", updated: "2026-11-01", parent: "industrial", langs: ES_EN, ogImage: og("industrial"), priority: "0.8", changefreq: "monthly",
    llms: { title: `Industrial · ${SECTOR_LABELS_ES[slug]}`, description: `Control de stock automático para ${SECTOR_LABELS_ES[slug].toLowerCase()}.` },
  })),
  { path: "recursos", kind: "hub", updated: "2026-11-01", langs: ES_EN, ogImage: og("retail"), priority: "0.7", changefreq: "weekly",
    llms: { title: "Recursos", description: "Guías sobre tiendas autónomas, precios y comparativas." } },
  { path: "casos", kind: "hub", updated: "2026-11-01", langs: ES_EN, ogImage: og("retail"), priority: "0.7", changefreq: "weekly",
    llms: { title: "Casos", description: "Casos reales de tiendas y almacenes autónomos con tecnología Techtail." } },
  { path: "estudio-implantacion", kind: "service", updated: "2026-11-01", langs: ES_EN, ogImage: og("retail"), priority: "0.9", changefreq: "monthly",
    llms: { title: "Estudio de implantación gratuito", description: "Envía tu plano o medidas y recibe una propuesta de distribución, superficie y equipamiento." } },
  { path: "retail/calculadora", kind: "tool", updated: "2026-11-01", parent: "retail", langs: ES_EN, ogImage: og("retail"), priority: "0.7", changefreq: "monthly",
    llms: { title: "Calculadora de rentabilidad Retail", description: "Estimación orientativa de ventas extra y ahorro de personal con supuestos editables." } },
];

/** Languages in which a route is indexable. */
export const routeLangs = (r: SeoRoute): Lang[] => r.langs ?? [...LANGS];
export const isIndexableIn = (r: SeoRoute | undefined, lang: Lang) => !r || (!r.draft && routeLangs(r).includes(lang));

/** Path of a route in a given language (translated slugs for resources/cases). */
export const pathFor = (r: SeoRoute, lang: Lang) => r.paths?.[lang] ?? r.path;

/** Finds a route by its path in any language. */
export const findRouteIn = (routes: SeoRoute[], basePath: string) => {
  const p = basePath.replace(/^\/+|\/+$/g, "");
  return routes.find((r) => r.path === p || (r.paths && Object.values(r.paths).includes(p)));
};

/** Static routes only; app code should use findRoute from ./allRoutes (includes resources/cases). */
export const findRoute = (basePath: string) => findRouteIn(SEO_ROUTES, basePath);

export const urlFor = (lang: string, path: string) =>
  `${SITE_URL}/${lang}${path ? `/${path}` : ""}`;
